import { PromiseToPayReason, PromiseToPayType } from '@dill/dill-shared';
import { AppInputField } from '../../../../../general/AppInputField/AppInputField';
import { DillDropdown } from '../../../../../general/Dill/DillDropdown';

// Embedded in AddCallModal/AddTextMessageModal so logging a call or text can also create a
// Promise to Pay in one step. `source` is deliberately NOT a field here - the parent modal
// already knows the right value (PHONE for a call, TEXT for a text) and sets it on submit.
export interface PromiseToPayFieldsValue {
  logPromise: boolean;
  type: PromiseToPayType | '';
  reason: PromiseToPayReason | '';
  reasonOther: string;
  promisedPaymentDate: string; // yyyy-mm-dd, matches <input type="date">
  amount: string;
}

export const defaultPromiseToPayFieldsValue: PromiseToPayFieldsValue = {
  logPromise: false,
  type: '',
  reason: '',
  reasonOther: '',
  promisedPaymentDate: '',
  amount: '',
};

export const isPromiseToPayFieldsValid = (value: PromiseToPayFieldsValue): boolean =>
  !value.logPromise || (!!value.type && !!value.reason && !!value.promisedPaymentDate);

const typeOptions: { value: PromiseToPayType; label: string }[] = [
  { value: 'FULL_PAYMENT', label: 'Full Payment' },
  { value: 'PARTIAL_PAYMENT', label: 'Partial Payment' },
  { value: 'PAYMENT_PLAN', label: 'Payment Plan' },
];

const reasonOptions: { value: PromiseToPayReason; label: string }[] = [
  { value: 'WAITING_ON_GC_PAYMENT', label: 'Waiting on GC Payment' },
  { value: 'CASH_FLOW_SHORTAGE', label: 'Cash Flow Shortage' },
  { value: 'MISSING_INVOICE_COPY_OR_STATEMENT', label: 'Missing Invoice Copy or Statement' },
  { value: 'NEEDS_LIEN_WAIVER', label: 'Needs Lien Waiver' },
  { value: 'RESOLVED_DISPUTE', label: 'Resolved Dispute' },
  { value: 'OTHER', label: 'Other' },
];

interface PromiseToPayFieldsSectionProps {
  value: PromiseToPayFieldsValue;
  onChange: (value: PromiseToPayFieldsValue) => void;
  isDisabled?: boolean;
}

export const PromiseToPayFieldsSection = ({
  value,
  onChange,
  isDisabled,
}: PromiseToPayFieldsSectionProps) => {
  const update = (patch: Partial<PromiseToPayFieldsValue>) => onChange({ ...value, ...patch });

  return (
    <div className="border border-gray-200 rounded-lg p-3">
      <label className="flex items-center gap-2 cursor-pointer select-none m-0">
        <input
          type="checkbox"
          checked={value.logPromise}
          onChange={(e) => update({ logPromise: e.target.checked })}
          disabled={isDisabled}
          className="w-4 h-4 accent-blue-500"
        />
        <span className="text-sm font-medium text-gray-700">Customer promised to pay</span>
      </label>

      {value.logPromise && (
        <div className="flex flex-col gap-4 mt-3">
          <div className="grid grid-cols-2 gap-4">
            <DillDropdown
              label="Payment Type"
              items={typeOptions}
              value={value.type || null}
              onSelectChange={(selected) =>
                update({ type: (selected?.value as PromiseToPayType) || '' })
              }
              disabled={isDisabled}
              placeholder="Select type"
              containerClassName="h-[42px]"
            />
            <AppInputField
              label="Promised Date"
              isRequired
              id="promise-date"
              inputType="date"
              value={value.promisedPaymentDate}
              onTextChange={(val) => update({ promisedPaymentDate: val })}
              isDisabled={isDisabled}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <DillDropdown
              label="Reason"
              items={reasonOptions}
              value={value.reason || null}
              onSelectChange={(selected) =>
                update({ reason: (selected?.value as PromiseToPayReason) || '' })
              }
              disabled={isDisabled}
              placeholder="Select reason"
              containerClassName="h-[42px]"
            />
            <AppInputField
              label="Amount"
              id="promise-amount"
              inputType="number"
              value={value.amount}
              onTextChange={(val) => update({ amount: val })}
              isDisabled={isDisabled}
              placeholder="Enter amount"
            />
          </div>

          {value.reason === 'OTHER' && (
            <AppInputField
              label="Other Reason"
              id="promise-reason-other"
              value={value.reasonOther}
              onTextChange={(val) => update({ reasonOther: val })}
              isDisabled={isDisabled}
              placeholder="Describe the reason"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PromiseToPayFieldsSection;
